import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Science as ScienceIcon,
  PlayArrow as PlayIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Quiz as QuizIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  useGetTrialLabOverviewQuery,
  useGetTrialLabHistoryQuery,
} from "@/store/api/user/trialLabApi";
import ExamStatusBadge from "@/components/common/ExamStatusBadge";

// Human-readable labels for source values returned by the API
const SOURCE_LABELS = {
  exercise: "Exercise",
  exam: "TOEIC Mock Test",
  "lc-exercise": "LC Exercise",
  "rc-exercise": "RC Exercise",
  "attendance-test": "Attendance Test",
};

const SOURCE_COLORS = {
  exercise: "default",
  exam: "primary",
  "lc-exercise": "info",
  "rc-exercise": "secondary",
  "attendance-test": "warning",
};

const getSourceLabel = (source) => SOURCE_LABELS[source] || source;
const getSourceColor = (source) => SOURCE_COLORS[source] || "default";

const TrialLaboratory = () => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sources: "exercise,exam",
    minIncorrectCount: 1,
    minIncorrectRate: "",
    testSize: 50,
  });

  const queryParams = useMemo(() => {
    const params = {};
    if (filters.sources) params.sources = filters.sources;
    if (filters.minIncorrectCount > 1)
      params.minIncorrectCount = filters.minIncorrectCount;
    if (filters.minIncorrectRate)
      params.minIncorrectRate = filters.minIncorrectRate;
    if (filters.testSize !== 20) params.testSize = filters.testSize;
    return params;
  }, [filters]);

  const {
    data: overviewData,
    isLoading,
    error,
  } = useGetTrialLabOverviewQuery(queryParams);

  const { data: historyData } = useGetTrialLabHistoryQuery({ limit: 100 });

  const overview = overviewData?.data;
  const miniTests = overview?.miniTests || [];
  const analytics = historyData?.data?.analytics;
  const attempts = historyData?.data?.attempts || [];

  // Map attempts by testIndex for quick lookup
  const attemptsByTest = useMemo(() => {
    const map = {};
    attempts.forEach((attempt) => {
      if (!map[attempt.testIndex]) map[attempt.testIndex] = [];
      map[attempt.testIndex].push(attempt);
    });
    return map;
  }, [attempts]);

  const handleStartTest = (testIndex) => {
    const params = new URLSearchParams();
    if (filters.sources !== "exercise,exam")
      params.set("sources", filters.sources);
    if (filters.minIncorrectCount > 1)
      params.set("minIncorrectCount", filters.minIncorrectCount);
    if (filters.minIncorrectRate)
      params.set("minIncorrectRate", filters.minIncorrectRate);
    if (filters.testSize !== 20) params.set("testSize", filters.testSize);

    const qs = params.toString();
    navigate(
      `/trial-laboratory/test/${testIndex}${qs ? `?${qs}` : ""}`
    );
  };

  const getTestStatus = (testIndex) => {
    const testAttempts = attemptsByTest[testIndex] || [];
    if (testAttempts.length === 0) return "new";
    const hasInProgress = testAttempts.some((a) => a.status === "in_progress");
    if (hasInProgress) return "in_progress";
    return "completed";
  };

  const getBestScore = (testIndex) => {
    const testAttempts = attemptsByTest[testIndex] || [];
    const completed = testAttempts.filter((a) => a.status === "completed");
    if (completed.length === 0) return null;
    return Math.max(...completed.map((a) => a.percentage));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e0f7fa 100%)",
        py: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
            color: "white",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <ScienceIcon sx={{ fontSize: 36 }} />
            <Typography variant="h4" fontWeight={700}>
              Trial Laboratory
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Practice your incorrectly answered questions in personalized mini
            tests
          </Typography>
        </Box>

        {/* Analytics Summary */}
        {analytics && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {overview?.totalIncorrectQuestions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Incorrect Questions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} color="info.main">
                  {overview?.totalMiniTests || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mini Tests
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="success.main"
                >
                  {analytics.completedAttempts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tests Completed
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="warning.main"
                >
                  {analytics.averagePercentage || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <FilterIcon color="action" />
              <Typography variant="subtitle1" fontWeight={600}>
                Filters
              </Typography>
            </Box>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showFilters}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sources</InputLabel>
                  <Select
                    value={filters.sources}
                    label="Sources"
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, sources: e.target.value }))
                    }
                  >
                    <MenuItem value="exercise,exam">All Sources</MenuItem>
                    <MenuItem value="exercise">Exercises Only (LC/RC/Attendance)</MenuItem>
                    <MenuItem value="exam">TOEIC Mock Tests Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load Trial Laboratory data. Please try again.
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !error && miniTests.length === 0 && (
          <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              No incorrect questions found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Great job! You haven't made any mistakes yet, or all your
              incorrect questions have been filtered out. Try adjusting the
              filters or practice more exercises and exams.
            </Typography>
          </Paper>
        )}

        {/* Mini Tests Grid */}
        {!isLoading && miniTests.length > 0 && (
          <Grid container spacing={3}>
            {miniTests.map((test) => {
              const status = getTestStatus(test.testIndex);
              const bestScore = getBestScore(test.testIndex);
              const testAttempts =
                attemptsByTest[test.testIndex]?.length || 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={test.testIndex}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                      },
                      border: "1px solid",
                      borderColor: "divider"
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <QuizIcon color="primary" />
                          <Typography variant="h6" fontWeight={700}>
                            Trial Test {test.testIndex + 1}
                          </Typography>
                        </Box>
                        {(status === "in_progress" ||
                          status === "completed") && (
                          <ExamStatusBadge status={status} showIcon />
                        )}
                        {status === "new" && (
                          <Chip label="New" size="small" color="info" />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {test.questionCount} questions
                      </Typography>

                      {/* Source chips */}
                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
                        {[
                          ...new Set(
                            test.questions.flatMap((q) => q.sources)
                          ),
                        ].map((source) => (
                          <Chip
                            key={source}
                            label={getSourceLabel(source)}
                            size="small"
                            variant="outlined"
                            color={getSourceColor(source)}
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </Box>

                      {/* Score info */}
                      {bestScore !== null && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Best Score
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color={
                                bestScore >= 80
                                  ? "success.main"
                                  : bestScore >= 60
                                  ? "warning.main"
                                  : "error.main"
                              }
                            >
                              {bestScore}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={bestScore}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "grey.200",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                bgcolor:
                                  bestScore >= 80
                                    ? "success.main"
                                    : bestScore >= 60
                                    ? "warning.main"
                                    : "error.main",
                              },
                            }}
                          />
                          {testAttempts > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {testAttempts} attempt
                              {testAttempts !== 1 ? "s" : ""}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<PlayIcon />}
                        onClick={() => handleStartTest(test.testIndex)}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          background:
                            status === "in_progress"
                              ? "linear-gradient(135deg, #29B6F6 0%, #0277BD 100%)"
                              : "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
                        }}
                      >
                        {status === "in_progress"
                          ? "Continue"
                          : status === "completed"
                          ? "Retry"
                          : "Start Test"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default TrialLaboratory;
